
import { apiService } from "core/services/apiService";
import { GenericResponse, ScreenGroup, ScreenGroupCreateData, ScreenGroupResponse } from "types";

// Fetch screen groups
export const fetchScreenGroups = async (workplaceId?: number): Promise<ScreenGroup[]> => {
    const id = workplaceId || Number(localStorage.getItem("workplace-id"));
    // Build query parameters
    const params = new URLSearchParams();
    params.append('pagination', 'false');
    if (id && !Number.isNaN(id)) {
        params.append('workplace', id.toString());
    }
    const response = await apiService.get<any>(
        `/game_master_screen_groups?${params.toString()}`
    );

    // Handle different response structures
    // When pagination=false, response might be direct array or { member: [...] }
    let items: any[] = [];
    if (Array.isArray(response.data)) {
        items = response.data;
    } else if (Array.isArray(response.data?.member)) {
        items = response.data.member;
    } else if (Array.isArray(response.data?.['hydra:member'])) {
        items = response.data['hydra:member'];
    }

    const mapped: ScreenGroup[] = items
        .map((item: any) => {
            const workplacePath: string = item?.workplace ?? ""; // e.g. "/api/workplaces/1"
            const workplaceIdFromPath = Number(String(workplacePath).split("/").pop());
            return {
                id: item.id,
                label: item.label,
                workplace: workplaceIdFromPath,
                list_order: item.listOrder,
                deleted: Boolean(item.deleted),
            } as ScreenGroup;
        })
        .filter((g: ScreenGroup) => !Number.isNaN(id) ? g.workplace === id : true)
        .sort((a: ScreenGroup, b: ScreenGroup) => a.list_order - b.list_order);

    // Store in localStorage with workplace-specific key
    localStorage.setItem(`screenGroups-${id}`, JSON.stringify(mapped));
    return mapped;
};


// create screen group
export const createScreenGroup = async (
    data: ScreenGroupCreateData
): Promise<ScreenGroupResponse> => {
    const payload = {
        label: data.label,
        workplace: `/api/workplaces/${data.workplace.id}`,
        listOrder: data.list_order,
        // deleted may be ignored by API on create; include if provided
        ...(typeof (data as any).deleted !== 'undefined' ? { deleted: (data as any).deleted } : {}),
    };
    const response = await apiService.post(`/game_master_screen_groups`, payload);
    return response.data;
};

export const deleteScreenGroup = async (
    screenGroupID: string
): Promise<void> => {
    await apiService.delete(`/game_master_screen_groups/${screenGroupID}`);
};


export const updateScreenGroup = async ({
    screenGroupID,
    data,
}: {
    screenGroupID: string;
    data: Partial<ScreenGroup>;
}): Promise<GenericResponse> => {
    const payload: any = {
        ...(typeof data.label !== 'undefined' ? { label: data.label } : {}),
        ...(typeof (data as any).list_order !== 'undefined' ? { listOrder: (data as any).list_order } : {}),
        ...(typeof data.deleted !== 'undefined' ? { deleted: data.deleted } : {}),
    };

    if (typeof (data as any).workplace !== 'undefined') {
        const w = (data as any).workplace;
        if (typeof w === 'number') {
            payload.workplace = `/api/workplaces/${w}`;
        } else if (typeof w === 'string') {
            // preserve if already in API IRI format
            payload.workplace = w.startsWith('/api/workplaces/') ? w : `/api/workplaces/${w}`;
        } else if (w && typeof w === 'object' && typeof w.id === 'number') {
            payload.workplace = `/api/workplaces/${w.id}`;
        }
    }

    const response = await apiService.put<GenericResponse>(
        `/game_master_screen_groups/${screenGroupID}`,
        payload
    );
    return response.data;
};